import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type AppRole = 
  | "admin" 
  | "senior_partner" 
  | "partner" 
  | "consultant" 
  | "client_scout";

// Legacy roles from verguetungsschema field
export type LegacyRole = 
  | "Client Scout" 
  | "Team Manager" 
  | "Field Executive";

interface UseUserRolesReturn {
  roles: AppRole[];
  isAdmin: boolean;
  isGesellschafter: boolean;
  isClientScout: boolean;
  isLoading: boolean;
  hasRole: (role: AppRole) => boolean;
  hasAnyRole: (...roles: AppRole[]) => boolean;
  hasAllRoles: (...roles: AppRole[]) => boolean;
}

/**
 * Hook for checking user roles with server-side validation
 * Uses react-query for caching and automatic refetching
 * 
 * @returns User role information and helper functions
 */
export function useUserRoles(): UseUserRolesReturn {
  const { user } = useAuth();

  const { data: userRoles, isLoading } = useQuery({
    queryKey: ["user-roles", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) throw error;
      return (data || []).map(r => r.role as AppRole);
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - semi-statische Stammdaten
    gcTime: 20 * 60 * 1000, // 20 minutes
  });

  const roles = userRoles || [];
  
  const hasRole = (role: AppRole): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (...rolesToCheck: AppRole[]): boolean => {
    return rolesToCheck.some(role => roles.includes(role));
  };

  const hasAllRoles = (...rolesToCheck: AppRole[]): boolean => {
    return rolesToCheck.every(role => roles.includes(role));
  };

  return {
    roles,
    isAdmin: hasRole("admin"),
    isGesellschafter: hasRole("senior_partner"),
    isClientScout: hasRole("client_scout"),
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };
}

/**
 * Legacy hook for backward compatibility
 * Combines old userRole logic with new roles system
 * TODO: Gradually migrate all uses to useUserRoles
 */
export function useUserRoleLegacy() {
  const { user } = useAuth();
  const { roles, isAdmin, isLoading } = useUserRoles();

  // Use React Query for partner role with proper caching
  const { data: userRole = null, isLoading: isLoadingPartner } = useQuery<LegacyRole | null>({
    queryKey: ["partner-verguetungsschema", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data: partner, error } = await supabase
        .from('vertriebspartner')
        .select('verguetungsschema')
        .eq('user_id', user.id)  // ID-first!
        .maybeSingle();          // Graceful handling of 0 rows

      if (error) {
        console.error('Error loading partner role:', error);
        return null;
      }

      return (partner?.verguetungsschema as LegacyRole) || null;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - semi-statische Stammdaten
    gcTime: 20 * 60 * 1000, // 20 minutes
  });

  // Commission rate calculation based on legacy role system
  const getCommissionRate = (): number => {
    switch (userRole) {
      case 'Client Scout':
        return 0.3;
      case 'Team Manager':
        return 0.2;
      case 'Field Executive':
        return 0.1;
      default:
        return 0;
    }
  };

  const getRoleColumnSuffix = (): string => {
    switch (userRole) {
      case 'Client Scout':
        return '_cs';
      case 'Team Manager':
        return '_tm';
      case 'Field Executive':
        return '_fe';
      default:
        return '';
    }
  };

  const calculateUserCommissionForContract = (contract: any): number => {
    if (!contract || !userRole) return 0;

    const suffix = getRoleColumnSuffix();
    const commissionField = `provision${suffix}`;
    const provision = contract[commissionField];

    return provision ? parseFloat(provision) : 0;
  };

  const getHighestRoleForContract = (contract: any): string | null => {
    if (!contract || !userRole) return null;

    const roleOrder = ['Client Scout', 'Team Manager', 'Field Executive'];
    const suffixMap = {
      'Client Scout': '_cs',
      'Team Manager': '_tm',
      'Field Executive': '_fe',
    };

    for (const role of roleOrder) {
      const suffix = suffixMap[role as keyof typeof suffixMap];
      const commissionField = `provision${suffix}`;
      if (contract[commissionField] && parseFloat(contract[commissionField]) > 0) {
        return role;
      }
    }

    return null;
  };

  return {
    userRole,
    isLoading: isLoading || isLoadingPartner,
    isAdmin,
    roles, // New: expose roles array
    getCommissionRate,
    getRoleColumnSuffix,
    calculateUserCommissionForContract,
    getHighestRoleForContract,
  };
}
